import { DEFAULT_AGENT_MODEL_ID } from '../../services/agent-models';
import { TicketsBoard, type KanbanColumn } from '@models';
import type { TicketMergeReadinessDetails } from '../../models/tickets-merge-readiness.model';
export type { KanbanColumn } from '@models';
export type { ChatMessage, RepoContext } from '../../models/orchestrator-surface.model';
export { compactChatMessages, normalizeRepoContext } from '../../models/orchestrator-surface.model';

const EXECUTION_MODEL_STORAGE_KEY = 'tickets.executionModelId';
const EXECUTION_RUN_STORAGE_KEY = 'tickets.executionRunId';

function executionModelStorageKey(projectId?: string | null): string {
  const scoped = String(projectId ?? '').trim();
  return scoped ? `${EXECUTION_MODEL_STORAGE_KEY}:${scoped}` : EXECUTION_MODEL_STORAGE_KEY;
}

function executionRunStorageKey(projectId?: string | null): string {
  const scoped = String(projectId ?? '').trim();
  return scoped ? `${EXECUTION_RUN_STORAGE_KEY}:${scoped}` : EXECUTION_RUN_STORAGE_KEY;
}

export function readPersistedExecutionModel(projectId?: string | null): string {
  try {
    const scoped = localStorage.getItem(executionModelStorageKey(projectId));
    if (String(scoped ?? '').trim()) return String(scoped).trim();
    const fallback = localStorage.getItem(EXECUTION_MODEL_STORAGE_KEY);
    return String(fallback ?? '').trim() || DEFAULT_AGENT_MODEL_ID;
  } catch {
    return DEFAULT_AGENT_MODEL_ID;
  }
}

export function persistExecutionModel(modelId: string, projectId?: string | null): void {
  const next = String(modelId ?? '').trim() || DEFAULT_AGENT_MODEL_ID;
  try {
    localStorage.setItem(EXECUTION_MODEL_STORAGE_KEY, next);
    const scopedKey = executionModelStorageKey(projectId);
    localStorage.setItem(scopedKey, next);
  } catch {}
}

export function readPersistedExecutionRunId(projectId?: string | null): string | null {
  try {
    const scoped = String(localStorage.getItem(executionRunStorageKey(projectId)) ?? '').trim();
    if (scoped) return scoped;
    const fallback = String(localStorage.getItem(EXECUTION_RUN_STORAGE_KEY) ?? '').trim();
    return fallback || null;
  } catch {
    return null;
  }
}

export function persistExecutionRunId(runId: string, projectId?: string | null): void {
  const next = String(runId ?? '').trim();
  if (!next) return;
  try {
    localStorage.setItem(EXECUTION_RUN_STORAGE_KEY, next);
    localStorage.setItem(executionRunStorageKey(projectId), next);
  } catch {}
}

export function clearPersistedExecutionRunId(projectId?: string | null): void {
  try {
    localStorage.removeItem(executionRunStorageKey(projectId));
  } catch {}
}

export type TicketsExecutionRuntime = {
  active: boolean;
  mode?: 'ticket' | 'build';
  runId?: string;
  ticketId: string;
  ticketTitle: string;
  repoFullName: string;
  routingReason?: string;
  routingConfidence?: number;
  model: string;
  phase:
    | 'idle'
    | 'queued'
    | 'planning'
    | 'starting'
    | 'executing'
    | 'validating_output'
    | 'creating_branch'
    | 'writing_files'
    | 'creating_pr'
    | 'workspace_prepare'
    | 'workspace_write'
    | 'workspace_verify'
    | 'workspace_repair'
    | 'workspace_commit'
    | 'running'
    | 'awaiting_input'
    | 'completed'
    | 'cancelled'
    | 'partial'
    | 'failed';
  startedAt: number | null;
  lastUpdateAt: number | null;
  pullRequestUrl?: string;
  progress?: {
    total: number;
    completed: number;
    failed: number;
    manualRequired: number;
    skipped: number;
  };
  workUnits?: Array<{
    id: string;
    owner: string;
    repo: string;
    role?: 'site' | 'api' | 'agent-api' | 'infra' | 'unknown';
    goal: string;
    status: string;
    skillId?: string;
    pullRequest?: { number: number; title: string; url: string };
  }>;
  latestCommands?: Array<{ id: string; phase: string; command: string; status: string; exitCode?: number; stdout?: string; stderr?: string }>;
  latestArtifacts?: Array<{ type: string; label: string; path?: string; content?: string }>;
  reviewAutomation?: { status: string; message?: string; updatedAt?: number };
  plannerRepoConfidence?: Array<{
    owner: string;
    repo: string;
    skillId?: string;
    verdict: 'high' | 'med' | 'low' | 'unknown';
    scorePct: number;
    reason: string;
    positives: number;
    negatives: number;
    neutrals: number;
    computedAt?: number;
  }>;
  mergeReadiness?: TicketMergeReadinessDetails & { state: string };
  latestEventSeq?: number;
  events?: Array<{
    seq: number;
    at: number;
    type: string;
    level?: string;
    phase?: string;
    title: string;
    message?: string;
    owner?: string;
    repo?: string;
    command?: string;
    status?: string;
    metadata?: any;
  }>;
  liveFollowConnected?: boolean;
  error?: string;
};

export function extractSchema(payload: any): any | null {
  const candidate = payload?.schema && typeof payload.schema === 'object' ? payload.schema : payload;
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return null;
  return candidate;
}

export function extractDiagram(payload: any): any | null {
  const candidate = payload?.diagram && typeof payload.diagram === 'object' ? payload.diagram : payload;
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return null;
  return candidate;
}

export function normalizeColumnKind(raw: any, title?: string): 'backlog' | 'execute' | 'review' | 'done' | 'custom' {
  const direct = String(raw ?? '').trim().toLowerCase();
  if (direct === 'backlog' || direct === 'execute' || direct === 'review' || direct === 'done') return direct;
  const byTitle = String(title ?? '').trim().toLowerCase();
  if (byTitle === 'backlog' || byTitle === 'execute' || byTitle === 'review' || byTitle === 'done') return byTitle;
  return 'custom';
}

function normalizeTicketNumber(raw: any): number | undefined {
  const parsed = typeof raw === 'number' ? raw : Number(String(raw ?? '').trim());
  if (!Number.isFinite(parsed) || parsed <= 0) return undefined;
  return Math.floor(parsed);
}

export function compareCardsByTicketNumber(left: any, right: any): number {
  const leftNumber = normalizeTicketNumber(left?.ticketNumber);
  const rightNumber = normalizeTicketNumber(right?.ticketNumber);
  if (leftNumber != null && rightNumber != null && leftNumber !== rightNumber) return leftNumber - rightNumber;
  if (leftNumber != null && rightNumber == null) return -1;
  if (leftNumber == null && rightNumber != null) return 1;
  return String(left?.title ?? '').localeCompare(String(right?.title ?? ''));
}

export function selectLatestBoardRecord(rows: TicketsBoard[]): TicketsBoard | null {
  const base = Array.isArray(rows) ? rows.filter(Boolean) : [];
  if (base.length === 0) return null;
  return (
    base
      .slice()
      .sort((left: any, right: any) => {
        const leftVersion = Number(left?.version ?? 0) || 0;
        const rightVersion = Number(right?.version ?? 0) || 0;
        if (leftVersion !== rightVersion) return rightVersion - leftVersion;
        const leftId = String(left?.id ?? left?._id ?? '').trim();
        const rightId = String(right?.id ?? right?._id ?? '').trim();
        return rightId.localeCompare(leftId);
      })[0] ?? null
  );
}

export function ensureIds(columns: KanbanColumn[]): KanbanColumn[] {
  const seenCols = new Set<string>();
  const seenCards = new Set<string>();

  const safeId = (base: string, seen: Set<string>) => {
    let id = base || '';
    if (!id) id = Math.random().toString(36).slice(2, 10);
    let candidate = id;
    let i = 1;
    while (seen.has(candidate)) {
      candidate = `${id}_${i++}`;
    }
    seen.add(candidate);
    return candidate;
  };

  return (columns ?? []).map((c, cIdx) => {
    const baseColId = (c?.id ?? `col_${cIdx}_${(c?.title ?? '').toLowerCase().replace(/\W+/g, '_')}`).slice(0, 60);
    const colId = safeId(baseColId, seenCols);
    const cards = (c?.cards ?? []).map((k, kIdx) => {
      const baseCardId = (k?.id ?? `card_${cIdx}_${kIdx}_${(k?.title ?? '').toLowerCase().replace(/\W+/g, '_')}`).slice(0, 80);
      const cardId = safeId(baseCardId, seenCards);
      return { ...k, id: cardId };
    });
    return { ...c, id: colId, kind: normalizeColumnKind((c as any)?.kind, (c as any)?.title), cards };
  });
}

export function normalizeBoard(columns: KanbanColumn[]): KanbanColumn[] {
  let board = ensureIds(columns ?? []);
  const assignTicketNumbers = (items: KanbanColumn[]): KanbanColumn[] => {
    const used = new Set<number>();
    let nextAvailable = 1;
    return (items ?? []).map((column) => ({
      ...column,
      cards: (column.cards ?? []).map((card) => {
        const existing = normalizeTicketNumber((card as any)?.ticketNumber);
        let ticketNumber = existing;
        if (!ticketNumber || used.has(ticketNumber)) {
          while (used.has(nextAvailable)) nextAvailable += 1;
          ticketNumber = nextAvailable;
        }
        used.add(ticketNumber);
        while (used.has(nextAvailable)) nextAvailable += 1;
        return { ...card, ticketNumber };
      }),
    }));
  };
  const sortWorkflowColumnCards = (items: KanbanColumn[]): KanbanColumn[] =>
    (items ?? []).map((column) => {
      const kind = normalizeColumnKind((column as any)?.kind, (column as any)?.title);
      if (kind !== 'backlog' && kind !== 'execute' && kind !== 'review' && kind !== 'done') {
        return column;
      }
      return {
        ...column,
        cards: [...(column.cards ?? [])].sort(compareCardsByTicketNumber),
      };
    });
  const ensureWorkflowColumn = (kind: 'backlog' | 'execute' | 'review' | 'done', title: string) => {
    const existingIdx = board.findIndex((col: any) => normalizeColumnKind(col?.kind, col?.title) === kind);
    if (existingIdx >= 0) {
      const existing = board[existingIdx]!;
      board[existingIdx] = {
        ...existing,
        kind,
        title: String(existing?.title ?? '').trim() || title,
      };
      return;
    }
    board = [...board, { id: '', title, kind, cards: [] as any[] } as any];
    board = ensureIds(board as any);
  };

  ensureWorkflowColumn('backlog', 'Backlog');
  ensureWorkflowColumn('execute', 'Execute');
  ensureWorkflowColumn('review', 'Review');
  ensureWorkflowColumn('done', 'Done');
  return sortWorkflowColumnCards(assignTicketNumbers(ensureIds(board)));
}
